/**
 * Single source of truth for IDP status display and chip colours.
 * Used by All IDPs page and Individual Development Plan page so chips stay consistent.
 */

import type { IdpStatus } from '../types/idp';

export type IdpStatusChipColor = 'default' | 'warning' | 'success' | 'info' | 'error';

const LABELS: Record<string, string> = {
  Draft: 'Draft',
  WithManager: 'With manager',
  Acknowledged: 'Acknowledged',
  Completed: 'Completed',
  NoIdp: 'No IDP',
};

const COLORS: Record<string, IdpStatusChipColor> = {
  Draft: 'default',
  WithManager: 'warning',
  Acknowledged: 'success',
  Completed: 'info',
  NoIdp: 'error',
};

function toNumber(status: IdpStatus | string | number): number | null {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    const n = parseInt(status, 10);
    if (!isNaN(n)) return n;
    const lower = status.toLowerCase();
    if (lower === 'draft') return 1;
    if (lower === 'withmanager' || lower === 'submitted') return 2;
    if (lower === 'acknowledged' || lower === 'active') return 3;
    if (lower === 'completed') return 4;
    if (lower === 'no idp' || lower === 'noidp') return 0; // Sentinel for "No IDP"
  }
  return null;
}

/** Human-readable label for an IDP status (string or numeric from API). */
export function formatIdpStatus(status: IdpStatus | string | number): string {
  if (typeof status === 'string' && LABELS[status]) return LABELS[status];
  const n = toNumber(status);
  if (n !== null) {
    switch (n) {
      case 0: return 'No IDP';
      case 1: return 'Draft';
      case 2: return 'With manager';
      case 3: return 'Acknowledged';
      case 4: return 'Completed';
      default: return 'Unknown';
    }
  }
  return 'Unknown';
}

/** MUI Chip color for IDP status. Each status has a distinct colour. */
export function getIdpStatusColor(status: IdpStatus | string | number): IdpStatusChipColor {
  if (typeof status === 'string' && COLORS[status]) return COLORS[status];
  const n = toNumber(status);
  if (n !== null) {
    switch (n) {
      case 0: return 'error';
      case 1: return 'default';
      case 2: return 'warning';
      case 3: return 'success';
      case 4: return 'info';
      default: return 'default';
    }
  }
  return 'default';
}

